import React, { useEffect, useState } from 'react';
import {
  PencilLine,
  Trash2,
  KeyRound,
  Search,
  X,
  PlusCircle
} from 'lucide-react';
import AddUserModal from './AddUserModal';
import ChangePasswordModal from './ChangePasswordModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import { authservice } from '../../../../components/api/authservice';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  roleID: string;
  roleName?: string;
}

interface UserManagementModalProps {
  onClose: () => void;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({ onClose }) => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await authservice.getUsers();
        setUsers(res || []);
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await authservice.deleteUser(userToDelete.id, userToDelete.tenantId);
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
    } catch (err) {
      console.error('Failed to delete user:', err);
    } finally {
      setUserToDelete(null);
    }
  };

  const handlePasswordUpdate = async (newPassword: string) => {
    if (!selectedUserForPassword) return;
    try {
      await authservice.updatePassword(selectedUserForPassword.id, newPassword, selectedUserForPassword.tenantId);
    } catch (err) {
      console.error('Failed to update password:', err);
    } finally {
      setSelectedUserForPassword(null);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.email.toLowerCase().includes(search.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(search.toLowerCase())
  );
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const refreshUserList = async () => {
    try {
      const res = await authservice.getUsers();
      setUsers(res || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    refreshUserList();
  }, []);
  // for Edit (from table row) 
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 text-[12px] font-sans">
      <div className="bg-neutral-900 text-white rounded-2xl shadow-2xl ring-1 ring-neutral-700 w-full max-w-4xl px-6 py-6 relative border border-neutral-800">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition">
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-base font-semibold mb-5 text-white">Users Management</h3>

        <div className="flex justify-between items-center mb-5">
          <button
            onClick={() => { setEditUserId(null); setShowUserModal(true); }}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-md text-sm font-medium transition"
          >
            <PlusCircle className="w-4 h-4" /> Add User
          </button>
          <div className="flex items-center bg-neutral-800 border border-neutral-700 rounded-md px-3 py-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent text-sm text-white placeholder-gray-500 ml-2 outline-none w-full"
            />
          </div>
        </div>

        <div className="overflow-x-auto max-h-80 border border-neutral-800 rounded-lg">
          <table className="w-full text-left text-sm text-gray-300">
            <thead className="bg-neutral-950 text-gray-400 uppercase text-xs">
              <tr>
                <th className="px-3 py-2 font-medium">Full Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-neutral-800/40 transition">
                  <td className="px-3 py-2 text-white">{user.firstName} {user.lastName}</td>
                  <td className="px-3 py-2 text-gray-300">{user.email}</td>
                  <td className="px-3 py-2 text-gray-400">{user.roleName ?? user.roleID}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-3">
                      <button onClick={() => { setEditUserId(user.id); setShowUserModal(true); }} title="Edit" className="text-gray-400 hover:text-indigo-400 transition">
                        <PencilLine className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setUserToDelete(user)}
                        title="Remove"
                        className="text-gray-400 hover:text-red-400 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedUserForPassword(user)}
                        title="Change Password"
                        className="text-gray-400 hover:text-yellow-400 transition"
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-6 text-gray-500">No users found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-right">
          <button
            onClick={onClose}
            className="bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-md text-white text-sm font-medium shadow-sm transition"
          >
            Done
          </button>
        </div>
      </div>

      {/* {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onSubmit={(newUser) => {
            setUsers(prev => [...prev, newUser]);
            setShowAddModal(false);
          }}
        />
      )} */}

      {showUserModal && (
        <AddUserModal
          onClose={() => setShowUserModal(false)}
          onSubmit={() => {
            setShowUserModal(false);
            refreshUserList(); // if needed
          }}
          userId={editUserId || undefined}
        />
      )}

      {selectedUserForPassword && (
        <ChangePasswordModal
          email={selectedUserForPassword.email}
          onClose={() => setSelectedUserForPassword(null)}
          onSubmit={handlePasswordUpdate}
        />
      )}

      {userToDelete && (
        <ConfirmDialog
          message={`Are you sure you want to delete user "${userToDelete.email}"?`}
          onYes={handleDeleteUser}
          onNo={() => setUserToDelete(null)}
          onCancel={() => setUserToDelete(null)}
        />
      )}
    </div>
  );
};

export default UserManagementModal;
